import { Button } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { ChangeEvent, useCallback } from 'react';
import { useTypedParams } from 'react-router-typesafe-routes/dom';
import { AppRoutes, useNavBack } from '~/app/app.routes';
import { AuthService } from '~/service/service.auth';
import { PromotionService } from '~/service/service.promotion';
import { UIService } from '~/service/service.ui';
import { PromotionCreateDto } from '~/shared/dto/admin/promotion.dto';
import { BrandId } from '~/shared/dto/id';
import { Form } from '~/shared/form';
import { useService } from '~/shared/service/service.base';
import { FieldAutocompleteSingle } from '~/view/fields/field.autocomplete';
import { FieldColorArray } from '~/view/fields/field.color';
import { FieldDate } from '~/view/fields/field.date';
import { FieldImage, FieldImageProps } from '~/view/fields/field.images';
import { FieldText } from '~/view/fields/field.text';
import { PromotionBestsellers } from '~/view/promotion/promotion.bestsellers';
import { PromotionColorPreview } from '~/view/promotion/promotion.color';
import { PromotionNewbies } from '~/view/promotion/promotion.newbies';
import { PromotionOffers } from '~/view/promotion/promotion.offers';
import { EditCenter, EditContainer, EditLeft, EditRight } from '~/view/view.edit';

export const PromotionEditPage = observer(() => {
	const [ui, auth, promotion] = useService(UIService, AuthService, PromotionService);
	const navBack = useNavBack();
	const { id } = useTypedParams(AppRoutes.PromotionEdit);

	const form = Form.use(PromotionCreateDto, {
		name: 'PromotionCreateDto',
		onSubmit: dto => promotion.save(id, dto).then(() => ui.notify('Promotion saved successfully', 'success')),
		initial: () => promotion.getOne(id),
	});

	const reload = useCallback(() => {
		promotion.getOne(id).then(data => data && form.apply(data));
	}, [form, id, promotion]);

	promotion.useBrands();
	promotion.useFragrances(form.$brandId.value);

	ui.usePageTitle(
		form.initial?.brandId
			? `Promotion - ${promotion.brands.find(b => b.id === form.initial?.brandId)?.name}`
			: 'Promotion new',
	);
	const canSave = id ? auth.can('promotion', 'UPDATE') : auth.can('promotion', 'CREATE');

	const handleChangeBrand = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const brandId = Number(e.target.value);
			form.$brandId.set(brandId as BrandId);
			form.$offers.set([]);
			form.$bestsellers.set([]);
			form.$newbies.set([]);
		},
		[form.$bestsellers, form.$brandId, form.$offers, form.$newbies],
	);

	return (
		<EditContainer async={promotion.async}>
			<EditLeft minWidth={300}>
				<FieldAutocompleteSingle
					items={promotion.brands.slice()}
					valueProp="id"
					labelProp="name"
					enabledProp="active"
					field={form.$brandId}
					disabled={!!id}
					onChange={handleChangeBrand}
				/>

				<FieldImage field={form.$logo} slotProps={fieldLogoProps.slotProps} />
				<FieldImage field={form.$logoDark} slotProps={fieldLogoProps.slotProps} />
				<FieldImage field={form.$bannerImage} slotProps={fieldBannerProps.slotProps} />
				<FieldDate view="day" field={form.$activeFrom} />
				<FieldDate view="day" field={form.$activeTo} />
			</EditLeft>
			<EditCenter minWidth={900} maxWidth={900}>
				<FieldText field={form.$description} multiline minRows={2} />
				<FieldColorArray field={form.$brandColors} maxColors={3} renderPreview={renderPromotionColorPreview} />
				<PromotionOffers field={form.$offers} />
				<PromotionBestsellers field={form.$bestsellers} />
				<PromotionNewbies field={form.$newbies} />
			</EditCenter>
			<EditRight>
				<Button variant="contained" onClick={() => form.submit()?.then(() => navBack())} disabled={!canSave}>
					Save and back
				</Button>
				{id && (
					<Button variant="outlined" onClick={() => form.submit()?.then(() => reload())} disabled={!canSave}>
						Save
					</Button>
				)}
				<Button variant="outlined" onClick={navBack}>
					Cancel
				</Button>
				<Button variant="outlined" onClick={form.reset}>
					Reset
				</Button>
				{id && (
					<Button
						disabled={!auth.can('promotion', 'DELETE')}
						variant="outlined"
						color="error"
						sx={{ mt: 2 }}
						onClick={() =>
							ui.confirmDelete(() =>
								promotion.delete(id).then(() => {
									navBack();
									ui.notify('Promotion deleted successfully', 'success');
								}),
							)
						}
					>
						Delete
					</Button>
				)}
			</EditRight>
		</EditContainer>
	);
});

function renderPromotionColorPreview(color: string) {
	return <PromotionColorPreview color={color} />;
}

function createFieldImageProps(aspectRatio = 2 / 1): FieldImageProps {
	return {
		slotProps: {
			label: { variant: 'caption', color: 'textSecondary' },
			paper: { elevation: 0, style: { padding: 0, width: '100%' } },
			dropDiv: {
				style: { width: '100%', height: 'auto', backgroundSize: 'cover', aspectRatio },
			},
		},
	};
}

const fieldLogoProps = createFieldImageProps();
const fieldBannerProps = createFieldImageProps(3 / 1);
